import type { ControlAccessStatus } from "./control-access";
/**
 * teleop-session — framework-free teleoperation transport package.
 *
 * Owns the entire infrastructure transport layer AND its own lifecycle:
 *   - WebSocket signaling (protoo) + reconnect policy
 *   - mediasoup WebRTC transports (send + recv)
 *   - remote media: builds a MediaStream per video slot and exposes it to the host
 *   - room consumer management + command sending
 *
 * It knows nothing about React, robot control, robot-config, telemetry or link-quality
 * ingestion — that's `teleop-state`'s job, wired on top via `attachSession` (which
 * subscribes to THIS module, never the other way around; see `teleop-state/session.ts`).
 *
 * `createTeleopSession()` returns a SINGLE LONG-LIVED object: create it once per
 * page/panel mount, and call `start()`/`stop()`/`reconnect()` on it as many times as
 * the host needs (explicit reconnect, room change, ...) — the object itself is never
 * recreated. Everything that used to be the host's job to manage across a "recreate a
 * disposable session object" cycle (a generation guard against stale in-flight starts,
 * keeping one `connectionQuality` monitor alive across reconnects, re-wiring listeners
 * on every cycle) is now internal and invisible to the caller. A subscription made
 * before the first `start()` (or between a `stop()` and the next `start()`) keeps
 * working across the cycle — nothing is torn down except the actual network resources.
 *
 * UI decoupling contract:
 *   - The returned object reads like a plain store — `getState()`/`subscribe(fn)`, no
 *     `setState` in the public type (nothing outside this module ever needs to write
 *     it) — PLUS action methods. Subscribe with `useStore` (React) or `.subscribe(...)`
 *     (framework-free); there is no separate `on`/`off` for state.
 *   - `phase`/`status`/`videoStalled` are STRING CODES, never human-readable labels —
 *     all label/localization belongs to the host app (see `lib/session-status-labels.ts`).
 *   - `phase` is authoritative for the whole lifecycle, INCLUDING a fatal signaling
 *     loss (`phase` becomes FAILED from the same single source — the host never needs
 *     to reconcile a separate "connection lost" stream into it itself; there is no
 *     public `connection_lost` event, since nothing outside this module needs the raw
 *     close detail — `phase: "failed"` already says everything a caller acts on).
 *   - `videoStalled` is derived and owned entirely inside this module (from
 *     NO_VIDEO_PRODUCER/VIDEO_SLOT_ACTIVE status codes, reset on a fresh
 *     STARTING/RECONNECTING cycle) — the host never pattern-matches status codes itself.
 *   - `log`/`operator_message`/`incoming_message`/`robot_info_requested`/
 *     `robot_info_stopped` are discrete EVENT STREAMS, not state — subscribed via
 *     `on(event, fn)`/`off(event, fn)`, mitt-based.
 *
 * Minimal public facade (index.ts) — the ONLY way to use this module from the app:
 *   createTeleopSession(staticOptions)   long-lived session object (see TeleopSession)
 *   resolveSessionConfig(base)           full config assembly incl. TURN credentials, from
 *                                         the base config loaded off /config.json
 *
 * `connectionQuality` (RTT/loss/transportState) and the mount→slot resolver are BOTH
 * per-session-object state: the quality monitor is created once inside
 * `createTeleopSession()` and lives for the object's whole lifetime (so its history
 * naturally survives every `start()`/`stop()` cycle without the host threading
 * anything); `resolveMountSlot` is a static constructor option. Never module-level
 * globals — that's what lets two independent sessions in the same tab (e.g. a
 * split-screen view) coexist without one clobbering the other's transport metrics or
 * camera-slot mapping.
 */

import mitt from "mitt";
import { createStore } from "zustand/vanilla";

import { startOperatorSession } from "./signaling/operator-session";
import type {
  OperatorSessionApi,
  OperatorSessionConfig,
  OperatorSessionOpts,
  VideoApi,
} from "./signaling/operator-session";
import { createConnectionQualityMonitor } from "../connection-stats";
import type { ConnectionQualityMonitor, ConnectionSnapshot } from "../connection-stats";
import { SessionPhase, STATUS_CODES } from "./status-codes";
import type { SessionPhaseValue, StatusCodeValue } from "./status-codes";
import type { ResolveMountSlot } from "./signaling/consumer-slots";

/** Static, rarely-changing construction options — everything that varies per `start()`
 * call (room/session id, resolved config, abort signal) is a `start()` argument instead. */
export interface CreateTeleopSessionOptions {
  resolveMountSlot?: ResolveMountSlot;
  persistPeerId?: boolean;
  /** XR input page receives DataChannels without subscribing to camera video. */
  receiveVideo?: boolean;
}

export interface StartSessionOptions {
  sessionId: string;
  config: OperatorSessionConfig;
  signal?: AbortSignal;
}

/** Reactive session state — subscribe via `useStore(session, selector)` or
 * `session.subscribe(...)`. Every field here is "the current value", never a stream. */
export interface SessionState {
  controlAccess: ControlAccessStatus;
  phase: SessionPhaseValue;
  /** Fine-grained status code; `null` until the first one arrives. */
  status: StatusCodeValue | null;
  /** True once joined with no video producer showing up within the wait window (see
   * STATUS_CODES.NO_VIDEO_PRODUCER) — cleared again the moment a video slot goes live,
   * or a fresh STARTING/RECONNECTING cycle begins. Reflects "currently stalled," not
   * "ever stalled." */
  videoStalled: boolean;
  videoApi: VideoApi | null;
  connectionQuality: { snapshot: ConnectionSnapshot; history: ConnectionSnapshot[] };
}

/** Event → payload map for the module's discrete (non-state) event streams. */
export type TeleopSessionEventMap = {
  /** Human-readable diagnostics/system log line. */
  log: { text: string; level?: string };
  /** A command/chat message produced by the operator towards the robot. */
  operator_message: { text: string };
  /** A raw inbound message from the robot (telemetry/config/etc). */
  incoming_message: unknown;
  /** Transport asks the host to start requesting robot info; provide a send(). */
  robot_info_requested: { send: (text: string) => boolean | void };
  /** Transport signals robot-info scheduling should stop (e.g. telemetry DC closed). */
  robot_info_stopped: void;
};

/**
 * Typed public facade returned by `createTeleopSession` — reads like a store
 * (`getState`/`subscribe`, read-only — no `setState`) plus action methods and the
 * discrete event streams.
 */
export interface TeleopSession {
  getState(): SessionState;
  getInitialState(): SessionState;
  subscribe(listener: (state: SessionState, previousState: SessionState) => void): () => void;
  /** Start (or restart) the session. Resolves once the operator has joined the room.
   * Superseding a previous still-in-flight `start()` (or a live session) is handled
   * internally — the caller never needs its own stop()-before-start() dance. */
  start(options: StartSessionOptions): Promise<void>;
  stop(): Promise<void>;
  /** Recover after a failure: reuses the transport's own `forceRejoin` if a session is
   * still live, otherwise replays the last `start()` options from scratch. */
  reconnect(): Promise<void>;
  authorizeControl(key: string): boolean;
  sendCommand(text: string): boolean;
  /** Send one operator-input frame on the unreliable `control` channel. Returns false
   * (frame dropped, never queued) while the channel is not open. */
  sendInput(text: string): boolean;
  on<K extends keyof TeleopSessionEventMap>(
    event: K,
    fn: (payload: TeleopSessionEventMap[K]) => void
  ): () => void;
  off<K extends keyof TeleopSessionEventMap>(
    event: K,
    fn: (payload: TeleopSessionEventMap[K]) => void
  ): void;
}

/** The three lifecycle fields derived from `onState`/`setStatus` — everything else in
 * `SessionState` (`videoApi`/`connectionQuality`) is set directly, not through events. */
interface LifecycleState {
  phase: SessionPhaseValue;
  status: StatusCodeValue | null;
  videoStalled: boolean;
}

type LifecycleEvent =
  { type: "phase"; phase: SessionPhaseValue } | { type: "status"; code: StatusCodeValue };

/**
 * Every rule for how `phase`/`status`/`videoStalled` change lives HERE, in one pure
 * function — not split across the two callback bodies that used to each own half of
 * `videoStalled`'s derivation. `start()`/`stop()` reset `LifecycleState` outright
 * instead of going through this (a fresh cycle, not an incremental transition).
 */
function reduceLifecycle(state: LifecycleState, event: LifecycleEvent): LifecycleState {
  if (event.type === "phase") {
    const entersFreshCycle =
      event.phase === SessionPhase.STARTING || event.phase === SessionPhase.RECONNECTING;
    return {
      ...state,
      phase: event.phase,
      videoStalled: entersFreshCycle ? false : state.videoStalled,
    };
  }
  let videoStalled = state.videoStalled;
  if (event.code === STATUS_CODES.NO_VIDEO_PRODUCER) videoStalled = true;
  else if (event.code === STATUS_CODES.VIDEO_SLOT_ACTIVE) videoStalled = false;
  return { ...state, status: event.code, videoStalled };
}

/** Pure translation of a `start()` call's inputs into the lower-level operator-session
 * config — no side effects, nothing to read in order, just a shape change. */
function toOperatorSessionConfig(
  options: StartSessionOptions,
  staticOptions: CreateTeleopSessionOptions
): OperatorSessionConfig {
  return {
    sessionId: options.sessionId,
    signalingWsBase: options.config?.signalingWsBase,
    iceCredentialUrl: options.config?.iceCredentialUrl,
    iceRefreshAt: options.config?.iceRefreshAt,
    controlAuthRequired: options.config?.controlAuthRequired,
    controlSid: options.config?.controlSid,
    iceServers: options.config?.iceServers || [],
    iceTransportPolicy: options.config?.iceTransportPolicy,
    diagnosticsWire: options.config?.diagnosticsWire,
    persistPeerId: staticOptions.persistPeerId,
    receiveVideo: staticOptions.receiveVideo,
  };
}

/** Wires the operator-session callback surface to this session object's emitter/state —
 * the only place that knows how a low-level operator-session event becomes a public
 * `SessionState` change or a discrete `TeleopSessionEventMap` event. */
function createOperatorSessionCallbacks(
  emitter: ReturnType<typeof mitt<TeleopSessionEventMap>>,
  dispatch: (event: LifecycleEvent) => void,
  staticOptions: CreateTeleopSessionOptions,
  connectionQuality: ConnectionQualityMonitor,
  signal?: AbortSignal
): OperatorSessionOpts {
  return {
    signal,
    appendDiag: (text, meta = {}) => emitter.emit("log", { text, level: meta.level }),
    setStatus: (code) => dispatch({ type: "status", code }),
    onState: (phase) => dispatch({ type: "phase", phase }),
    appendOperatorDcMessage: (text) => emitter.emit("operator_message", { text }),
    appendSocketIoDcMessage: (message) => emitter.emit("incoming_message", message),
    // Same principle as videoStalled: the single point of truth for a fatal signaling
    // loss lives HERE, inside the module — `phase` becomes FAILED from this one place.
    // No public event: nothing outside the module has ever needed the raw close detail,
    // only the fact that the session failed.
    onSignalingLost: () => dispatch({ type: "phase", phase: SessionPhase.FAILED }),
    onCapabilitiesRequested: (payload) => emitter.emit("robot_info_requested", payload),
    onCapabilitiesStopped: () => emitter.emit("robot_info_stopped"),
    resolveMountSlot: staticOptions.resolveMountSlot ?? null,
    connectionQuality,
  };
}

/**
 * Create a long-lived teleoperation session object — see the module doc comment above.
 */
export function createTeleopSession(staticOptions: CreateTeleopSessionOptions = {}): TeleopSession {
  const emitter = mitt<TeleopSessionEventMap>();
  const connectionQuality: ConnectionQualityMonitor = createConnectionQualityMonitor();

  let generation = 0;
  let api: OperatorSessionApi | null = null;
  let lastStartOptions: StartSessionOptions | null = null;
  let lifecycle: LifecycleState = { phase: SessionPhase.IDLE, status: null, videoStalled: false };

  const store = createStore<SessionState>(() => ({
    ...lifecycle,
    videoApi: null,
    connectionQuality: { snapshot: connectionQuality.getConnectionSnapshot(), history: [] },
    controlAccess: "local",
  }));

  /** Run an event through `reduceLifecycle` and publish the result — the ONLY path by
   * which `onState`/`setStatus`/`onSignalingLost` reach the store. */
  function dispatch(event: LifecycleEvent) {
    lifecycle = reduceLifecycle(lifecycle, event);
    store.setState(lifecycle);
  }

  /** Start a fresh generation: bumps the race-guard counter and returns it, so the
   * caller can later check `isStale(myGeneration)` after an await. */
  function beginGeneration(): number {
    generation += 1;
    return generation;
  }
  function isStale(myGeneration: number): boolean {
    return generation !== myGeneration;
  }

  connectionQuality.onConnectionSnapshot((snapshot) => {
    store.setState({
      connectionQuality: { snapshot, history: connectionQuality.getConnectionHistory() },
    });
  });

  async function stop(): Promise<void> {
    beginGeneration();
    const current = api;
    api = null;
    dispatch({ type: "phase", phase: SessionPhase.STOPPED });
    if (current?.stop) await current.stop();
    store.setState({ videoApi: null });
    // Deliberately no `emitter.all.clear()` here: this object is reused for the next
    // start() (see module doc comment) — subscriptions made by the host (e.g.
    // teleop-state's attachSession, a log passthrough) must survive this cycle.
  }

  async function start(options: StartSessionOptions): Promise<void> {
    lastStartOptions = options;
    const myGeneration = beginGeneration();

    const previous = api;
    api = null;
    if (previous?.stop) await previous.stop();

    // A fresh cycle resets outright — not an incremental transition through
    // reduceLifecycle, which only knows how to react to individual phase/status events.
    lifecycle = { phase: SessionPhase.STARTING, status: null, videoStalled: false };
    store.setState(lifecycle);

    const cfg = toOperatorSessionConfig(options, staticOptions);
    const opts = createOperatorSessionCallbacks(
      emitter,
      dispatch,
      staticOptions,
      connectionQuality,
      options.signal
    );

    opts.onControlAccess = (status) => {
      if (!isStale(myGeneration)) store.setState({ controlAccess: status });
    };
    try {
      const nextApi = await startOperatorSession(cfg, opts);
      if (isStale(myGeneration)) {
        // Superseded by a stop()/start() cycle that ran while this one was still
        // connecting — fully live at this point but no longer ours, so tear it down
        // here rather than leave it running unowned.
        if (nextApi?.stop) void nextApi.stop();
        return;
      }
      api = nextApi;
      store.setState({ videoApi: nextApi?.videoApi ?? null });
    } catch (err) {
      if (isStale(myGeneration)) return;
      dispatch({ type: "phase", phase: SessionPhase.FAILED });
      throw err;
    }
  }

  async function reconnect(): Promise<void> {
    if (api?.forceRejoin) {
      await api.forceRejoin();
      return;
    }
    if (lastStartOptions) await start(lastStartOptions);
  }

  function sendCommand(text: string): boolean {
    return api?.sendOperatorCommand ? api.sendOperatorCommand(text) : false;
  }

  function sendInput(text: string): boolean {
    return api?.sendOperatorInput ? api.sendOperatorInput(text) : false;
  }

  return {
    getState: store.getState,
    getInitialState: store.getInitialState,
    subscribe: store.subscribe,
    start,
    stop,
    reconnect,
    authorizeControl: (key: string) => api?.authorizeControl?.(key) ?? false,
    sendCommand,
    sendInput,
    on: (event, fn) => {
      emitter.on(event, fn);
      return () => emitter.off(event, fn);
    },
    off: (event, fn) => emitter.off(event, fn),
  };
}

export { resolveSessionConfig } from "./sessionConfig";
export type { ResolvedSessionConfig, RawSessionConfig } from "./sessionConfig";
export { SessionPhase, STATUS_CODES } from "./status-codes";
export type { SessionPhaseValue, StatusCodeValue } from "./status-codes";
export type { ConnectionSnapshot, ResolvedLinkLevel } from "../connection-stats";
export type { VideoApi } from "./signaling/operator-session";
export type { SlotMetrics } from "./media/video-pipeline";
export type { VideoLatencyMode, VideoComparison, VideoComparisonSummary } from "./media/video-latency-control";
